import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import TablaConsolidado from '../shared/TablaConsolidado';
import DashboardService from '../../../services/DashboardService';
import NivelService from '../../../services/LevelService';
import GradoService from '../../../services/GradesService';
import { useAuth } from '../../../context/AuthContext';
import { cargarIe } from '../../../components/buscador/sesion.search';
import ResumenGlobalSesiones from '../shared/ResumenGlobalSesiones';

export default function DashboardDirector() {
    const { user } = useAuth();
    const [catalogo, setCatalogo] = useState(null);

    const [opcionesIIEE, setOpcionesIIEE] = useState([]);
    const [institucionFiltro, setInstitucionFiltro] = useState('');

    const [saeGeneradas, setSaeGeneradas] = useState([]);
    const [sacGeneradas, setSacGeneradas] = useState([]);
    const [saeNoGeneradas, setSaeNoGeneradas] = useState([]);
    const [sacNoGeneradas, setSacNoGeneradas] = useState([]);
    const [cargando, setCargando] = useState(true);

    useEffect(() => {
        const cargarMolde = async () => {
            try {
                const { data: niveles } = await NivelService.listarNiveles();

                const columnasSet = new Set();
                const areasMap = new Map();
                const estructuraNiveles = [];

                for (const nivel of niveles) {
                    const { data: grados } = await NivelService.listarGradosPorNivel(nivel.id);
                    const { data: areas } = await GradoService.listarAreasPorNivel(nivel.id);

                    (areas || []).forEach((a) => {
                        if (!a?.sigla) return;

                        const sigla = String(a.sigla).trim();
                        const nombreArea = String(a.nombre || a.descripcion || a.sigla).trim();

                        columnasSet.add(sigla);

                        if (!areasMap.has(sigla)) {
                            areasMap.set(sigla, {
                                sigla,
                                nombres: [],
                            });
                        }

                        const areaActual = areasMap.get(sigla);

                        if (nombreArea && !areaActual.nombres.includes(nombreArea)) {
                            areaActual.nombres.push(nombreArea);
                        }
                    });

                    estructuraNiveles.push({
                        nombre: nivel.nombre,
                        grados: grados.map(g => g.descripcion).sort((a, b) => a - b)
                    });
                }

                setCatalogo({
                    columnas: Array.from(columnasSet).sort(),
                    niveles: estructuraNiveles,
                    areasTematicas: Array.from(areasMap.values()).sort((a, b) =>
                        a.sigla.localeCompare(b.sigla)
                    ),
                });
            } catch (error) {
                console.error("Error cargando el catálogo oficial:", error);
            }
        };

        cargarMolde();
    }, []);

    useEffect(() => {
        const fetchColegiosDelDistrito = async () => {
            if (user?.ubicacion?.ubigeo) {
                try {
                    const response = await cargarIe({ codigo_ubigeo: user.ubicacion.ubigeo });
                    const dataLimpia = Array.isArray(response) ? response : (response?.data || []);
                    setOpcionesIIEE(dataLimpia);
                } catch (error) {
                    console.error("Error al cargar los colegios del distrito:", error);
                }
            }
        };

        fetchColegiosDelDistrito();
    }, [user]);

    useEffect(() => {
        const cargarDatos = async () => {
            setCargando(true);
            try {
                const paramsParaBackend = institucionFiltro
                    ? { id_institucion_edu: institucionFiltro }
                    : {};

                const [resSaeGen, resSacGen, resSaeNoGen, resSacNoGen] = await Promise.all([
                    DashboardService.obtenerConsolidado({ ...paramsParaBackend, tipo_sesion: 'SAE', estado: 'producido' }),
                    DashboardService.obtenerConsolidado({ ...paramsParaBackend, tipo_sesion: 'SAC', estado: 'producido' }),
                    DashboardService.obtenerConsolidado({ ...paramsParaBackend, tipo_sesion: 'SAE', estado: 'guardado' }),
                    DashboardService.obtenerConsolidado({ ...paramsParaBackend, tipo_sesion: 'SAC', estado: 'guardado' })
                ]);

                setSaeGeneradas(resSaeGen);
                setSacGeneradas(resSacGen);
                setSaeNoGeneradas(resSaeNoGen);
                setSacNoGeneradas(resSacNoGen);
            } catch (error) {
                console.error("Error al traer los datos de las tablas:", error);
            } finally {
                setCargando(false);
            }
        };

        cargarDatos();
    }, [institucionFiltro]);

    const ieSeleccionada = opcionesIIEE.find(
        (ie) => String(ie.id) === String(institucionFiltro)
    );

    const ambitoTitulo = ieSeleccionada
        ? `INSTITUCIÓN EDUCATIVA ${ieSeleccionada.nombre}`
        : `DISTRITO ${user?.ubicacion?.distrito || ''}`;

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 'xl',
                minHeight: '100vh',
                px: { xs: 0, md: 2, lg: 6 },
                mx: 'auto',
            }}
        >
            <Box sx={{ mb: 4, p: 3, bgcolor: '#ffffff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                <FormControl fullWidth size="small" sx={{ maxWidth: 500 }}>
                    <InputLabel shrink id="label-iiee-filtro">
                        Filtrar por Institución Educativa
                    </InputLabel>
                    <Select
                        labelId="label-iiee-filtro"
                        value={institucionFiltro}
                        label="Filtrar por Institución Educativa"
                        onChange={(e) => setInstitucionFiltro(e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value=""><em>Todas las II.EE.</em></MenuItem>
                        {opcionesIIEE.map(ie => (
                            <MenuItem key={ie.id} value={ie.id}>
                                {ie.nombre} ({ie.nivel_modalidad})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {cargando || !catalogo ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Box sx={{ mt: 2, mb: 4, p: 3, bgcolor: '#ffffff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <ResumenGlobalSesiones
                            sacGeneradas={sacGeneradas}
                            sacNoGeneradas={sacNoGeneradas}
                            saeGeneradas={saeGeneradas}
                            saeNoGeneradas={saeNoGeneradas}
                            areasTematicas={catalogo?.areasTematicas || []}
                            ambitoTitulo={ambitoTitulo}
                        />
                    </Box>

                    <Box sx={{ mt: 2, mb: 4, p: 3, bgcolor: '#ffffff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <Typography variant="h5" fontWeight="900" color="#0277bd" sx={{ mb: 3 }}>
                            Sesiones de Aprendizaje Contextualizadas (SAC) Generadas
                        </Typography>
                        <TablaConsolidado tipo="SAC" catalogo={catalogo} datosPlanos={sacGeneradas} />

                        <Typography variant="h5" fontWeight="900" color="#e65100" sx={{ mb: 3 }}>
                            Sesiones de Aprendizaje Contextualizadas (SAC) En Proceso
                        </Typography>
                        <TablaConsolidado tipo="SAC" catalogo={catalogo} datosPlanos={sacNoGeneradas} />
                    </Box>

                    <Box sx={{ mb: 4, p: 3, bgcolor: '#ffffff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <Typography variant="h5" fontWeight="900" color="#0277bd" sx={{ mb: 3 }}>
                            Sesiones de Aprendizaje Educativas (SAE) Generadas
                        </Typography>
                        <TablaConsolidado tipo="SAE" catalogo={catalogo} datosPlanos={saeGeneradas} />

                        <Typography variant="h5" fontWeight="900" color="#e65100" sx={{ mb: 3 }}>
                            Sesiones de Aprendizaje Educativas (SAE) En Proceso
                        </Typography>
                        <TablaConsolidado tipo="SAE" catalogo={catalogo} datosPlanos={saeNoGeneradas} />
                    </Box>
                </>
            )}
        </Box>
    );
}
